import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import {
  FiArrowLeft,
  FiCheckCircle,
  FiAlertCircle,
  FiExternalLink,
  FiSave,
  FiEye,
  FiSend
} from 'react-icons/fi';
import { blogApi, blogSite, uploadBlogImage } from './api';
import { analyzeSeo, makeSlug } from './seo';
const RichEditor = lazy(() => import('./RichEditor'));
export const emptyPost = (locale) => ({
  title: '',
  slug: '',
  locale: locale || 'en',
  content: '',
  excerpt: '',
  author: '',
  categories: [],
  tags: [],
  featuredImage: { url: '', alt: '' },
  status: 'draft',
  publishAt: '',
  seo: {
    title: '',
    description: '',
    focusKeyword: '',
    canonical: '',
    index: true,
    schemaType: 'BlogPosting',
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
    twitterCard: 'summary_large_image'
  },
  commentsEnabled: true,
  moderateComments: true
});
const localTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
export default function PostEditor({ initial, authors, terms, onBack, onSaved, suggestions }) {
  const [form, setForm] = useState(() => ({
    ...emptyPost(),
    ...initial,
    seo: { ...emptyPost().seo, ...initial.seo },
    publishAt: localTime(initial.publishAt)
  }));
  const [baseline, setBaseline] = useState(() => JSON.stringify(form));
  const [tab, setTab] = useState('content'),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [notice, setNotice] = useState(''),
    [uploading, setUploading] = useState(false),
    [preview, setPreview] = useState('');
  const [analyzed, setAnalyzed] = useState(form);
  const dirty = JSON.stringify(form) !== baseline;
  const savedStatus = JSON.parse(baseline).status;
  useEffect(() => {
    const t = setTimeout(() => setAnalyzed(form), 250);
    return () => clearTimeout(t);
  }, [form]);
  useEffect(() => {
    if (!preview) return;
    const close = (event) => {
      if (event.key === 'Escape') setPreview('');
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [preview]);
  const analysis = useMemo(() => analyzeSeo(analyzed), [analyzed]);
  useEffect(() => {
    const warn = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setNotice('');
  };
  const seo = (key, value) => setForm((f) => ({ ...f, seo: { ...f.seo, [key]: value } }));
  const changeTitle = (value) =>
    setForm((f) => ({
      ...f,
      title: value,
      ...(!f.everPublished && (!f.slug || f.slug === makeSlug(f.title))
        ? { slug: makeSlug(value) }
        : {})
    }));
  const leave = () => {
    if (!dirty || window.confirm('You have unsaved changes. Discard them?')) onBack();
  };
  const payload = (status = form.status) => ({
    ...form,
    status,
    publishAt:
      status === 'scheduled' && form.publishAt ? new Date(form.publishAt).toISOString() : null
  });
  const save = async (status) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const saved = await blogApi('/admin/posts' + (form._id ? '/' + form._id : ''), {
        method: form._id ? 'PUT' : 'POST',
        body: payload(status)
      });
      const next = { ...saved, publishAt: localTime(saved.publishAt) };
      setForm(next);
      setBaseline(JSON.stringify(next));
      setNotice(
        saved.status === 'draft'
          ? 'Draft saved.'
          : saved.status === 'scheduled'
            ? 'Article scheduled.'
            : 'Article published.'
      );
      onSaved(saved);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const previewPost = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await blogApi('/admin/preview', { method: 'POST', body: payload() });
      setPreview(data.html);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const image = await uploadBlogImage(file);
      setForm((f) => ({ ...f, featuredImage: { ...f.featuredImage, url: image.url } }));
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };
  const toggleTerm = (key, id) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(id) ? f[key].filter((v) => v !== id) : [...f[key], id]
    }));
  const localeTerms = terms.filter((t) => t.locale === form.locale);
  const checks = tab === 'readability' ? analysis.readability : analysis.checks;
  return (
    <div className="blog-editor">
      <div className="blog-toolbar">
        <button className="blog-button ghost" onClick={leave}>
          <FiArrowLeft /> All articles
        </button>
        <span className="blog-muted">
          {dirty ? 'Unsaved changes' : form._id ? 'All changes saved' : 'New draft'} ·{' '}
          {analysis.words} words · {analysis.readingMinutes} min read
        </span>
        <div className="blog-actions">
          <button className="blog-button secondary" onClick={previewPost} disabled={busy}>
            <FiEye /> Preview
          </button>
          <button
            type="button"
            className="blog-button secondary"
            onClick={() => save('draft')}
            disabled={busy || uploading}
          >
            <FiSave /> {savedStatus === 'published' ? 'Unpublish to draft' : 'Save draft'}
          </button>
          {form.status === 'scheduled' && (
            <button
              type="button"
              className="blog-button secondary"
              onClick={() => save('scheduled')}
              disabled={busy || uploading}
            >
              <FiSave /> Save schedule
            </button>
          )}
          <button
            type="button"
            className="blog-button"
            onClick={() => save('published')}
            disabled={busy || uploading}
          >
            <FiSend />{' '}
            {savedStatus === 'published'
              ? 'Publish changes'
              : form.status === 'scheduled'
                ? 'Publish now'
                : 'Publish'}
          </button>
        </div>
      </div>
      {error && (
        <div className="blog-alert error" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="blog-alert success" role="status">
          {notice}
        </div>
      )}
      <div className="blog-editor-grid">
        <section className="blog-panel blog-composer">
          <label className="blog-field">
            Article title
            <input
              className="blog-title-input"
              dir={form.locale === 'ur' ? 'rtl' : 'ltr'}
              value={form.title}
              maxLength={200}
              onChange={(e) => changeTitle(e.target.value)}
              placeholder={
                form.locale === 'ur' ? 'مضمون کا عنوان' : 'Give your article a clear title'
              }
            />
          </label>
          <div className="blog-permalink">
            <span>
              {blogSite}/blog/{form.locale}/
            </span>
            <input
              aria-label="Article slug"
              value={form.slug}
              maxLength={160}
              disabled={form.everPublished}
              onChange={(e) => update('slug', makeSlug(e.target.value))}
            />
            <span>{form.everPublished ? 'Published URL locked' : 'Auto-generated · editable'}</span>
          </div>
          <div className="blog-tabs" role="tablist" aria-label="Article editor sections">
            {['content', 'seo', 'readability', 'social', 'settings'].map((name) => (
              <button
                key={name}
                role="tab"
                aria-selected={tab === name}
                onClick={() => setTab(name)}
              >
                {name === 'seo' ? 'SEO' : name[0].toUpperCase() + name.slice(1)}
              </button>
            ))}
          </div>
          {tab === 'content' && (
            <div className="blog-tab-content">
              <Suspense fallback={<p>Loading editor…</p>}>
                <RichEditor
                  value={form.content}
                  onChange={(value) => update('content', value)}
                  locale={form.locale}
                />
              </Suspense>
              <label className="blog-field">
                Excerpt
                <textarea
                  rows={3}
                  maxLength={500}
                  value={form.excerpt}
                  onChange={(e) => update('excerpt', e.target.value)}
                  placeholder="A short introduction for the article listing. Generated from the content if left blank."
                />
              </label>
              <div className="blog-inline-panel">
                <h3>Internal linking suggestions</h3>
                <p className="blog-muted">
                  Link to a relevant published article. Copy its URL and add it with the editor’s
                  link tool.
                </p>
                {suggestions
                  .filter(
                    (p) =>
                      p.locale === form.locale &&
                      p._id !== form._id &&
                      p.effectiveStatus === 'published'
                  )
                  .slice(0, 5)
                  .map((p) => (
                    <div className="blog-suggestion" key={p._id}>
                      <span>{p.title}</span>
                      <button
                        className="blog-button secondary"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(
                              `${blogSite}/blog/${p.locale}/${encodeURIComponent(p.slug)}`
                            );
                            setNotice(
                              'Article URL copied. Select relevant text and use the link tool.'
                            );
                          } catch {
                            setNotice(`${blogSite}/blog/${p.locale}/${p.slug}`);
                          }
                        }}
                      >
                        Copy link
                      </button>
                    </div>
                  ))}
                <a href={`${blogSite}/blog/${form.locale}`} target="_blank" rel="noreferrer">
                  Browse published articles <FiExternalLink />
                </a>
              </div>
            </div>
          )}
          {tab === 'seo' && (
            <div className="blog-tab-content">
              <label className="blog-field">
                Focus keyword
                <input
                  maxLength={120}
                  value={form.seo.focusKeyword}
                  onChange={(e) => seo('focusKeyword', e.target.value)}
                  placeholder="The main phrase this article covers"
                />
              </label>
              <label className="blog-field">
                SEO title{' '}
                <span className={form.seo.title.length > 60 ? 'blog-warning' : 'blog-muted'}>
                  {form.seo.title.length} / 60 recommended
                </span>
                <input
                  maxLength={120}
                  value={form.seo.title}
                  onChange={(e) => seo('title', e.target.value)}
                  placeholder={form.title || 'Title shown in search results'}
                />
              </label>
              <label className="blog-field">
                Meta description{' '}
                <span className={form.seo.description.length > 160 ? 'blog-warning' : 'blog-muted'}>
                  {form.seo.description.length} / 160 recommended
                </span>
                <textarea
                  rows={3}
                  maxLength={320}
                  value={form.seo.description}
                  onChange={(e) => seo('description', e.target.value)}
                  placeholder="Explain what readers will learn and why they should click."
                />
              </label>
              <div className="blog-search-preview" dir={form.locale === 'ur' ? 'rtl' : 'ltr'}>
                <small>
                  {blogSite}/blog/{form.locale}/{form.slug || 'article-slug'}
                </small>
                <h3>{form.seo.title || form.title || 'Your search result title'}</h3>
                <p>
                  {form.seo.description ||
                    'Your meta description will appear here. Search engines may choose a different excerpt.'}
                </p>
              </div>
              <p className="blog-muted">
                Keyword occurrences: {analysis.occurrences} · Approximate density:{' '}
                {analysis.density}%
              </p>
            </div>
          )}
          {tab === 'readability' && (
            <div className="blog-tab-content">
              <h2>Write for your reader</h2>
              <p>{analysis.localeNote}</p>
              <p className="blog-muted">
                These editorial checks help you review your writing. They are not an official
                reading grade or search ranking signal.
              </p>
            </div>
          )}
          {(tab === 'seo' || tab === 'readability') && (
            <div className="blog-checks">
              {checks.map((check) => (
                <div className={check.pass ? 'pass' : 'improve'} key={check.label}>
                  {check.pass ? <FiCheckCircle /> : <FiAlertCircle />}
                  <div>
                    <strong>{check.label}</strong>
                    {!check.pass && <p>{check.advice}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {tab === 'social' && (
            <div className="blog-tab-content">
              <p className="blog-muted">
                Leave fields blank to use the SEO title, description and cover image.
              </p>
              {[
                ['ogTitle', 'Open Graph title', 120],
                ['ogDescription', 'Open Graph description', 320],
                ['ogImage', 'Open Graph image URL', 2000],
                ['twitterTitle', 'Twitter / X title', 120],
                ['twitterDescription', 'Twitter / X description', 320],
                ['twitterImage', 'Twitter / X image URL', 2000]
              ].map(([key, label, max]) => (
                <label className="blog-field" key={key}>
                  {label}
                  <input
                    type={key.endsWith('Image') ? 'url' : 'text'}
                    maxLength={max}
                    value={form.seo[key]}
                    onChange={(e) => seo(key, e.target.value)}
                  />
                </label>
              ))}
              <label className="blog-field">
                Twitter Card
                <select
                  aria-label="Twitter Card"
                  value={form.seo.twitterCard}
                  onChange={(e) => seo('twitterCard', e.target.value)}
                >
                  <option value="summary_large_image">Large image summary</option>
                  <option value="summary">Summary</option>
                </select>
              </label>
            </div>
          )}
          {tab === 'settings' && (
            <div className="blog-tab-content">
              <label className="blog-field">
                Canonical URL override
                <input
                  type="url"
                  value={form.seo.canonical}
                  onChange={(e) => seo('canonical', e.target.value)}
                  placeholder={`${blogSite}/blog/${form.locale}/${form.slug}`}
                />
              </label>
              <p className="blog-muted">
                Normally leave this blank to use this article’s URL. An external canonical excludes
                the article from the blog sitemap.
              </p>
              <label className="blog-field">
                Structured data type
                <select
                  aria-label="Structured data type"
                  value={form.seo.schemaType}
                  onChange={(e) => seo('schemaType', e.target.value)}
                >
                  <option>BlogPosting</option>
                  <option>Article</option>
                  <option>NewsArticle</option>
                </select>
              </label>
              <label className="blog-checkbox">
                <input
                  type="checkbox"
                  checked={form.seo.index}
                  onChange={(e) => seo('index', e.target.checked)}
                />{' '}
                Allow search engines to index this article
              </label>
              <label className="blog-checkbox">
                <input
                  type="checkbox"
                  checked={form.commentsEnabled}
                  onChange={(e) => update('commentsEnabled', e.target.checked)}
                />{' '}
                Allow comments
              </label>
              <label className="blog-checkbox">
                <input
                  type="checkbox"
                  checked={form.moderateComments}
                  onChange={(e) => update('moderateComments', e.target.checked)}
                />{' '}
                Review comments before they appear
              </label>
            </div>
          )}
        </section>
        <aside className="blog-editor-aside">
          <section className="blog-panel">
            <div className="blog-score-row">
              <div className={`blog-score ${analysis.score >= 70 ? 'good' : ''}`}>
                {analysis.score}
                <span>/ 100</span>
              </div>
              <div>
                <h3>SEO health</h3>
                <p className="blog-muted">
                  {analysis.checks.filter((c) => c.pass).length} of {analysis.checks.length} checks
                  passed
                </p>
              </div>
            </div>
            <p className="blog-muted">Editorial guidance, not a ranking guarantee.</p>
            <div className="blog-readability">
              <span>Readability checks</span>
              <strong>{analysis.readabilityScore}%</strong>
            </div>
          </section>
          <section className="blog-panel">
            <h3>Publishing</h3>
            <label className="blog-field">
              Status
              <select
                aria-label="Status"
                value={form.status}
                onChange={(e) => update('status', e.target.value)}
              >
                <option value="draft">Draft / Unpublished</option>
                <option value="scheduled">Scheduled</option>
                <option value="published">Published</option>
              </select>
            </label>
            {form.status === 'scheduled' && (
              <label className="blog-field">
                Publish at (your local time)
                <input
                  type="datetime-local"
                  required
                  value={form.publishAt}
                  onChange={(e) => update('publishAt', e.target.value)}
                />
                <small className="blog-muted">
                  Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
                </small>
              </label>
            )}
            <label className="blog-field">
              Author
              <select
                aria-label="Author"
                value={form.author}
                onChange={(e) => update('author', e.target.value)}
              >
                <option value="">Current editor</option>
                {authors.map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.name} ({a.role === 'admin' ? 'Super Admin' : 'Manager'})
                  </option>
                ))}
              </select>
            </label>
            {form._id && form.status === 'published' && (
              <a
                className="blog-public-link"
                target="_blank"
                rel="noreferrer"
                href={`${blogSite}/blog/${form.locale}/${encodeURIComponent(form.slug)}`}
              >
                View public article <FiExternalLink />
              </a>
            )}
          </section>
          <section className="blog-panel">
            <h3>Cover image</h3>
            {form.featuredImage.url && (
              <img
                className="blog-cover-preview"
                src={form.featuredImage.url}
                alt={form.featuredImage.alt || 'Cover preview'}
              />
            )}
            <label className="blog-field">
              Upload cover
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={uploading}
                onChange={uploadCover}
              />
            </label>
            {uploading && <p role="status">Uploading cover…</p>}
            <label className="blog-field">
              Image URL
              <input
                type="url"
                value={form.featuredImage.url}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    featuredImage: { ...f.featuredImage, url: e.target.value }
                  }))
                }
                placeholder="https://…"
              />
            </label>
            <label className="blog-field">
              Alt text
              <input
                value={form.featuredImage.alt}
                maxLength={300}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    featuredImage: { ...f.featuredImage, alt: e.target.value }
                  }))
                }
                placeholder="Describe the cover image"
              />
            </label>
          </section>
          {[
            ['categories', 'category', 'Categories'],
            ['tags', 'tag', 'Tags']
          ].map(([key, kind, heading]) => (
            <section className="blog-panel" key={key}>
              <h3>{heading}</h3>
              <div className="blog-term-options">
                {localeTerms
                  .filter((t) => t.kind === kind)
                  .map((term) => (
                    <label className="blog-checkbox" key={term._id}>
                      <input
                        type="checkbox"
                        checked={form[key].includes(term._id)}
                        onChange={() => toggleTerm(key, term._id)}
                      />
                      {term.name}
                    </label>
                  ))}
              </div>
              {!localeTerms.some((t) => t.kind === kind) && (
                <p className="blog-muted">
                  Create {heading.toLowerCase()} in the Categories & tags tab first.
                </p>
              )}
            </section>
          ))}
        </aside>
      </div>
      {preview && (
        <div className="blog-modal" role="dialog" aria-modal="true" aria-label="Article preview">
          <div className="blog-modal-content">
            <div className="blog-toolbar">
              <h2>Private preview</h2>
              <button className="blog-button secondary" autoFocus onClick={() => setPreview('')}>
                Close preview
              </button>
            </div>
            <iframe title="Article preview" sandbox="" srcDoc={preview} />
          </div>
        </div>
      )}
    </div>
  );
}
